import React from "react";

import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import PermPhoneMsgIcon from "@mui/icons-material/PermPhoneMsg";
import PersonIcon from "@mui/icons-material/Person";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { Avatar, Divider, IconButton, Popover, Stack, Tooltip, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useNavigate, useRoleGuardHook } from "hooks";
import { useAccountSelector, useAuthActions, useUserSelector } from "store";
import { IUser } from "store/types";

import { RoleName } from "../../../constants";
import { AccountsForSwitch } from "./Accounts-for-switch";

const triggerTooltipText = "Account";

export const Profile: React.FC = () => {
  const account = useAccountSelector();
  const isSocialEnabled = account?.allow_social;
  const dataroomPhoneNumber = account?.dataroom_phone_number;
  const user: IUser = useUserSelector();

  const navigate = useNavigate();
  const { logoutAction } = useAuthActions();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const goToSocial = useRoleGuardHook(() => {
    if (!isSocialEnabled) return;
    navigate({ route: "socialProfile" });
  }, [RoleName.Admin, RoleName.Analyst]);

  const logoutWithRedirect = async () => {
    handleClose();
    await logoutAction();
    navigate({ route: "main" }); // Reset route after logout
  };

  const tooltipTitle = account || user ? (
    <Stack sx={{ gap: "6px", minWidth: "200px" }}>
      {account && (
        <>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0A3142" }}>
            {account.name}
          </Typography>
          {account.domain && (
            <Stack direction="row" alignItems="center" gap="6px">
              <LanguageIcon sx={{ fontSize: "14px", color: "#5D7782" }} />
              <Typography sx={{ fontSize: "12px", color: "#5D7782" }}>
                {account.domain}
              </Typography>
            </Stack>
          )}
          {dataroomPhoneNumber && (
            <Stack direction="row" alignItems="center" gap="6px" sx={{ mt: "4px" }}>
              <Tooltip title="Incoming sms phone number">
                <PermPhoneMsgIcon sx={{ fontSize: "14px", color: "#5D7782" }} />
              </Tooltip>
              <Typography sx={{ fontSize: "12px", color: "#5D7782" }}>
                {dataroomPhoneNumber}
              </Typography>
            </Stack>
          )}
        </>
      )}
      {user && account && <Divider sx={{ my: "4px", borderColor: "#DAE0E2" }} />}
      {user && (
        <>
          <Stack direction="row" alignItems="center" gap="6px">
            <PersonIcon sx={{ fontSize: "14px", color: "#5D7782" }} />
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#0A3142" }}>
              {user.full_name}
            </Typography>
          </Stack>
          {user.email && (
            <Stack direction="row" alignItems="center" gap="6px">
              <EmailIcon sx={{ fontSize: "14px", color: "#5D7782" }} />
              <Typography sx={{ fontSize: "12px", color: "#5D7782" }}>
                {user.email}
              </Typography>
            </Stack>
          )}
          {user.role?.presentation_name && (
            <Typography sx={{ fontSize: "12px", color: "#5D7782", fontStyle: "italic" }}>
              {user.role.presentation_name}
            </Typography>
          )}
        </>
      )}
    </Stack>
  ) : (
    triggerTooltipText
  );

  return (
    <>
      <MaterialTooltip isShow title={tooltipTitle}>
        <IconButton sx={{ width: "30px", height: "30px" }} onClick={handleClick}>
          <Avatar sx={{ width: "28px", height: "28px" }} src={user?.profile_photo_url} />
        </IconButton>
      </MaterialTooltip>
      <Popover
        transformOrigin={{ horizontal: 220, vertical: "bottom" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        <Stack sx={{ pt: "10px", maxHeight: "600px", overflow: "hidden", maxWidth: "220px" }}>
          <Stack direction="row" alignItems="center" sx={{ p: "12px", gap: "12px", borderBottom: "1px solid #DAE0E2" }}>
            <Avatar sx={{ width: "24px", height: "24px" }} src={user?.profile_photo_url} />
            <Stack>
              <Typography
                sx={{
                  color: "#0A3142",
                  fontSize: "16px",
                  fontWeight: 400,
                  ...(Boolean(goToSocial) && { cursor: "pointer" }),
                  lineHeight: "inherit"
                }}
                onClick={goToSocial}
              >
                {user?.full_name}
              </Typography>
              <MaterialTooltip title={user?.email || ""} isShow>
                <Typography
                  noWrap
                  sx={{
                    color: "#5D7782",
                    fontSize: "13px",
                    fontWeight: 400,
                    maxWidth: "200px",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {user?.email}
                </Typography>
              </MaterialTooltip>
              <Typography sx={{ color: "#5D7782", fontSize: "13px", fontWeight: 600 }}>
                {user?.role?.presentation_name}
              </Typography>
            </Stack>
          </Stack>
          <AccountsForSwitch />
          <Stack sx={{ bgcolor: "#0A3142", p: "12px" }} direction="row" justifyContent="space-between">
            <Typography
              onClick={logoutWithRedirect}
              sx={{ color: "white", fontSize: "16px", fontWeight: 400, cursor: "pointer" }}
            >
              Sign Out
            </Typography>
            <TuneOutlinedIcon
              onClick={() => {
                handleClose();
                navigate({ route: "profile" });
              }}
              sx={{ fill: "white", cursor: "pointer" }}
            />
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
